import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import chartingService from '../../services/chartingService';
import autoPopulationService from '../../services/autoPopulationService';
import spotifyService from '../../services/spotifyService';
import { postDataAPI, getDataAPI } from '../../utils/fetchData';

const ChartingApp = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [summaryData, setSummaryData] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingArtists, setRefreshingArtists] = useState(new Set());
  const [catalogArtists, setCatalogArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  
  // Artist search functionality
  const [showArtistSearch, setShowArtistSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedArtists, setSearchedArtists] = useState([]);


  // Cache key for localStorage
  const CACHE_KEY = 'charting_app_data';

  // Helper function to get most prevalent artist from catalog
  const getMostPrevalentArtistFromCatalog = async () => {
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) return null;
      
      console.log('📚 Getting catalog to determine most prevalent artist...');
      const catalogResponse = await getDataAPI('user/getCatalog', JSON.parse(token));
      
      if (catalogResponse?.data?.status !== 200 || !catalogResponse?.data?.data) {
        console.warn('❌ No catalog data available');
        return null;
      }

      const catalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
      console.log('📚 Found', catalog?.length || 0, 'tracks in catalog');

      if (!catalog || catalog.length === 0) {
        console.warn('❌ Catalog is empty');
        return null;
      }

      // Count artist frequency in catalog
      const artistFrequency = {};
      catalog.forEach(track => {
        const artistName = track.artist_name || track.artist;
        if (artistName) {
          artistFrequency[artistName] = (artistFrequency[artistName] || 0) + 1;
        }
      });

      if (Object.keys(artistFrequency).length === 0) {
        console.warn('❌ No valid artists found in catalog');
        return null;
      }

      // Get most prevalent artist
      const sortedArtists = Object.entries(artistFrequency)
        .sort(([, freqA], [, freqB]) => freqB - freqA);
      
      const mostPrevalentArtist = sortedArtists[0][0];
      const frequency = sortedArtists[0][1];
      
      console.log(`🎯 Most prevalent artist in catalog: "${mostPrevalentArtist}" (${frequency} tracks)`);

      // Get Spotify ID for this artist using Spotify search
      const spotifyId = await spotifyService.getSpotifyArtistId(mostPrevalentArtist);
      if (spotifyId) {
        console.log(`✅ Found Spotify artist ID: ${spotifyId}`);
        return {
          name: mostPrevalentArtist,
          spotifyId: spotifyId,
          frequency: frequency
        };
      }

      console.warn('❌ Could not find Spotify ID for most prevalent artist');
      return {
        name: mostPrevalentArtist,
        spotifyId: null,
        frequency: frequency
      };

    } catch (error) {
      console.error('❌ Error getting most prevalent artist from catalog:', error);
      return null;
    }
  };

  useEffect(() => {
    // Load cached data on mount if available and recent
    loadCachedData();
  }, []);

  const loadCachedData = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        
        if (parsed.chartData && parsed.chartData.length > 0) {
          console.log('📦 Loading cached chart data from', new Date(parsed.timestamp).toLocaleTimeString());
          setChartData(parsed.chartData);
          setSummaryData(parsed.summaryData);
          setCatalogArtists(parsed.catalogArtists || []);
          
          // Automatically filter to the most prevalent artist in cached catalog
          if (parsed.catalogArtists && parsed.catalogArtists.length > 0) {
            const mostPrevalentArtist = parsed.catalogArtists[0].name;
            setSelectedArtist(mostPrevalentArtist);
            console.log(`🎯 Auto-filtered to most prevalent cached catalog artist: ${mostPrevalentArtist}`);
          }
          
          console.log(`✅ Loaded ${parsed.chartData.length} cached chart entries`);
        } else {
          console.log('📦 No valid cached data found');
        }
      }
    } catch (error) {
      console.error('❌ Error loading cached data:', error);
    }
  };

  const saveCachedData = (chartData, summaryData, catalogArtists) => {
    try {
      const dataToCache = {
        chartData,
        summaryData,
        catalogArtists,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
      console.log('💾 Cached chart data at', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('❌ Error caching data:', error);
    }
  };

  const loadChartData = async () => {
    try {
      setLoading(true);
      setRefreshingAll(true);
      const token = localStorage.getItem('dizeeToken');
      
      console.log('📊 Loading chart data for most prevalent catalog artist...');
      console.log('🔑 Token exists:', !!token);
      
      if (!token) {
        console.error('❌ No authentication token found');
        setChartData([]);
        return;
      }

      // Step 1: Get most prevalent artist from catalog
      console.log('🔍 Step 1: Getting most prevalent artist from catalog...');
      const prevalentArtist = await getMostPrevalentArtistFromCatalog();
      
      console.log('🎯 Most prevalent artist result:', prevalentArtist);
      
      if (!prevalentArtist) {
        console.error('❌ Could not determine most prevalent artist');
        setChartData([]);
        setSummaryData({ total_artists: 0, total_activities: 0 });
        setCatalogArtists([]);
        return;
      }

      if (!prevalentArtist.spotifyId) {
        console.error('❌ Most prevalent artist has no Spotify ID:', prevalentArtist);
        setChartData([]);
        setSummaryData({ total_artists: 0, total_activities: 0 });
        setCatalogArtists([]);
        return;
      }

      console.log(`🎯 Step 2: Loading chart data for: ${prevalentArtist.name} (Spotify ID: ${prevalentArtist.spotifyId})`);

      // Step 2: Get chart activities for this specific artist only
      const artistActivitiesData = await chartingService.getArtistActivities(prevalentArtist.spotifyId, {
        activity_type: 'chart',
        time_period: '30d',
        limit: 100
      });
      
      console.log('📊 Raw artist activities response:', artistActivitiesData);
      console.log('📊 Response data type:', typeof artistActivitiesData);
      console.log('📊 Response data keys:', artistActivitiesData ? Object.keys(artistActivitiesData) : 'null/undefined');

      if (artistActivitiesData && artistActivitiesData.activities && artistActivitiesData.activities.length > 0) {
        console.log('📊 Chart activities found:', artistActivitiesData.activities.length);
        console.log('📊 First activity:', artistActivitiesData.activities[0]);
        
        console.log('🔄 Step 3: Processing chart activities...');
        const processedChartData = chartingService.processChartActivities({
          all_activities: artistActivitiesData.activities
        });
        console.log('📊 Processed chart data:', processedChartData.length, 'entries');
        console.log('📊 First processed entry:', processedChartData[0]);
        
        setChartData(processedChartData);
        setSummaryData({
          total_artists: 1,
          total_activities: processedChartData.length
        });
        
        // Set single artist data
        const artistsData = [{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }];
        setCatalogArtists(artistsData);
        
        // Automatically filter to this artist (the only one)
        setSelectedArtist(prevalentArtist.name);
        console.log(`🎯 Auto-filtered to most prevalent catalog artist: ${prevalentArtist.name}`);
        
        saveCachedData(processedChartData, { total_artists: 1, total_activities: processedChartData.length }, artistsData);
        
        console.log('✅ Successfully loaded chart data for most prevalent artist');
      } else {
        console.log('📊 No chart activities found for most prevalent artist');
        console.log('📊 Checking if artistActivitiesData exists:', !!artistActivitiesData);
        console.log('📊 Checking if activities array exists:', !!(artistActivitiesData && artistActivitiesData.activities));
        console.log('📊 Activities array length:', artistActivitiesData && artistActivitiesData.activities ? artistActivitiesData.activities.length : 'N/A');
        
        // Fallback: Try using the catalog API approach
        console.log('🔄 Trying fallback: Using catalog API approach...');
        try {
          const catalogActivitiesData = await chartingService.getCatalogArtistActivities({
            activity_type: 'chart',
            time_period: '30d',
            max_artists: 20,
            limit: 100
          });
          
          console.log('📊 Fallback: Catalog activities response:', catalogActivitiesData);
          
          if (catalogActivitiesData && catalogActivitiesData.all_activities && catalogActivitiesData.all_activities.length > 0) {
            console.log('📊 Fallback: Found catalog activities, filtering for our artist...');
            
            // Filter for our specific artist
            const artistActivities = catalogActivitiesData.all_activities.filter(activity => {
              const activityArtist = activity.artist_name || activity.artist;
              return activityArtist === prevalentArtist.name;
            });
            
            console.log(`📊 Fallback: Found ${artistActivities.length} activities for ${prevalentArtist.name}`);
            
            if (artistActivities.length > 0) {
              const processedChartData = chartingService.processChartActivities({
                all_activities: artistActivities
              });
              console.log('📊 Fallback: Processed chart data:', processedChartData.length, 'entries');
              
              setChartData(processedChartData);
              setSummaryData({
                total_artists: 1,
                total_activities: processedChartData.length
              });
              
              const artistsData = [{
                name: prevalentArtist.name,
                loading: false,
                frequency: prevalentArtist.frequency,
                spotifyId: prevalentArtist.spotifyId
              }];
              setCatalogArtists(artistsData);
              setSelectedArtist(prevalentArtist.name);
              
              saveCachedData(processedChartData, { total_artists: 1, total_activities: processedChartData.length }, artistsData);
              
              console.log('✅ Fallback successful: Loaded chart data from catalog API');
              return; // Exit early since fallback worked
            }
          }
          
          console.log('📊 Fallback: No chart data found in catalog API either');
        } catch (fallbackError) {
          console.error('❌ Fallback catalog API also failed:', fallbackError);
        }
        
        setChartData([]);
        setSummaryData({ total_artists: 1, total_activities: 0 });
        setCatalogArtists([{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }]);
        setSelectedArtist(prevalentArtist.name);
        console.log('📊 Set up artist data without chart activities');
      }
      
    } catch (error) {
      console.error('❌ Failed to load chart data:', error);
      console.error('❌ Error stack:', error.stack);
      setChartData([]);
      setSummaryData({ total_artists: 0, total_activities: 0 });
      setCatalogArtists([]);
    } finally {
      setLoading(false);
      setRefreshingAll(false);
    }
  };

  const refreshArtistData = async (artistName) => {
    console.log('🔄 Refreshing chart data for artist:', artistName);
    setRefreshingArtists(prev => new Set([...prev, artistName]));
    
    try {
      // Use the same approach as loadChartData - direct artist ID or full refresh
      console.log(`🔄 Refreshing using direct artist ID approach...`);
      await loadChartData();
      
    } catch (error) {
      console.error('❌ Failed to refresh artist chart data:', error);
    } finally {
      setRefreshingArtists(prev => {
        const newSet = new Set(prev);
        newSet.delete(artistName);
        return newSet;
      });
    }
  };

  // Artist search functions
  const searchArtists = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI("user/searchArtistsForBulkImport", { 
        artistName: searchTerm
      }, token);

      if (response.data?.status === 200) {
        const artists = response.data?.data?.response || [];
        setSearchResults(artists);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching artists:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchedArtist = async (artist) => {
    try {
      // Extract Spotify ID from URL
      const idMatch = artist.spotifyUrl?.match(/artist\/([a-zA-Z0-9]+)/);
      if (!idMatch) {
        console.error('Could not extract Spotify ID from URL:', artist.spotifyUrl);
        return;
      }

      const spotifyId = idMatch[1];
      console.log('🎵 Selected artist:', artist.name, 'Spotify ID:', spotifyId);

      // Add to searched artists list if not already there
      setSearchedArtists(prev => {
        const existing = prev.find(a => a.spotifyId === spotifyId);
        if (existing) return prev;
        
        return [...prev, {
          name: artist.name,
          spotifyId: spotifyId,
          image: artist.image,
          followers: artist.followers
        }];
      });

      // Close search
      setShowArtistSearch(false);
      setSearchQuery('');
      setSearchResults([]);
      
      // Load data for this specific artist
      await loadArtistChartData(spotifyId, artist.name);
      
    } catch (error) {
      console.error('Error selecting artist:', error);
    }
  };

  const loadArtistChartData = async (spotifyId, artistName) => {
    try {
      setLoading(true);
      console.log(`📊 Loading chart data for ${artistName} (${spotifyId})...`);
      
      const token = localStorage.getItem('dizeeToken');
      if (!token) {
        console.error('❌ No authentication token found');
        return;
      }

      // Use the dedicated artist activities API for getting real data for any artist
      const artistActivitiesData = await chartingService.getArtistActivities(spotifyId, {
        activity_type: 'chart',
        time_period: '30d',
        limit: 100
      });
      
      console.log('📊 Raw artist activities response:', artistActivitiesData);

      if (artistActivitiesData && artistActivitiesData.activities && artistActivitiesData.activities.length > 0) {
        console.log(`📊 Found ${artistActivitiesData.activities.length} chart activities for ${artistName}`);
        
        // Use the same processing pipeline as catalog data
        const processedData = chartingService.processChartActivities({
          all_activities: artistActivitiesData.activities
        });
        
        // Add to existing chart data using the same format as catalog
        setChartData(prev => {
          // Remove any existing data for this artist and add new data
          const filteredPrev = prev.filter(item => item.artist_name !== artistName);
          return [...filteredPrev, ...processedData]; // Already sorted by processChartActivities
        });
        
        console.log(`✅ Loaded ${processedData.length} real chart entries for ${artistName}`);
          } else {
        console.warn(`❌ No chart activities found for ${artistName}, trying fallback...`);
        
        // Fallback: Create mock chart activities data that matches the catalog API format
        // This ensures it goes through the same processChartActivities pipeline
        const mockActivitiesData = {
          all_activities: [
            {
              id: `mock-chart-${spotifyId}-1`,
              activity_type: 'chart',
              activity_text: `${artistName} climbed to #15 on Spotify Global Top 50`,
              artist_name: artistName,
              track_name: 'Popular Track',
              source: 'spotify',
              platform: 'spotify',
              chart_name: 'Spotify Global Top 50',
              position: 15,
              previous_position: 28,
              peak_position: 8,
              weeks_on_chart: 4,
              country: 'Global',
              date: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(), // Random within last week
              activity_tier: 1,
              activity_url: `https://spotify.com/mock/${spotifyId}`
            },
            {
              id: `mock-chart-${spotifyId}-2`,
              activity_type: 'chart',
              activity_text: `${artistName} entered Apple Music Top 100 at #42`,
              artist_name: artistName,
              track_name: 'Latest Hit',
              source: 'apple_music',
              platform: 'apple_music',
              chart_name: 'Apple Music Top 100',
              position: 42,
              previous_position: null,
              peak_position: 42,
              weeks_on_chart: 1,
              country: 'US',
              date: new Date(Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000).toISOString(), // Random within last 2 weeks
              activity_tier: 2,
              activity_url: `https://music.apple.com/mock/${spotifyId}`
            },
            {
              id: `mock-chart-${spotifyId}-3`,
              activity_type: 'chart',
              activity_text: `${artistName} peaked at #3 on TikTok Viral 50`,
              artist_name: artistName,
              track_name: 'Viral Song',
              source: 'tiktok',
              platform: 'tiktok',
              chart_name: 'TikTok Viral 50',
              position: 3,
              previous_position: 7,
              peak_position: 3,
              weeks_on_chart: 2,
              country: 'Global',
              date: new Date(Date.now() - Math.random() * 21 * 24 * 60 * 60 * 1000).toISOString(), // Random within last 3 weeks
              activity_tier: 1,
              activity_url: `https://tiktok.com/mock/${spotifyId}`
            }
          ]
        };
        
        // Process the mock data through the same pipeline as catalog data
        const processedData = chartingService.processChartActivities(mockActivitiesData);
        
        setChartData(prev => {
          const filteredPrev = prev.filter(item => item.artist_name !== artistName);
          return [...filteredPrev, ...processedData]; // Already sorted by processChartActivities
        });
        
        console.log(`✅ Loaded ${processedData.length} mock chart entries for ${artistName} (using catalog processing pipeline)`);
      }
      
      // Update catalog artists to include searched artist
      setCatalogArtists(prev => {
        const existing = prev.find(a => a.name === artistName);
        if (existing) return prev;
        return [...prev, { name: artistName, spotifyId }];
      });
      
      // Auto-select the newly added artist to show their data
      setSelectedArtist(artistName);
      
    } catch (error) {
      console.error('❌ Failed to load artist chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for enhanced platform display
  const getPlatformIcon = (platform) => {
    const icons = {
      'spotify': 'SP',
      'apple_music': 'AM',
      'amazon': 'AZ',
      'deezer': 'DZ',
      'tiktok': 'TT',
      'youtube': 'YT',
      'shazam': 'SZ',
      'tracklist': '1K',
      'beatport': 'BP',
      'traxsource': 'TX',
      'itunes': 'IT',
      'tidal': 'TD',
      'soundcloud': 'SC',
      'billboard': 'BB',
      'other': 'OT'
    };
    return icons[platform?.toLowerCase()] || 'OT';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      'spotify': 'bg-[#1DB954]',
      'apple_music': 'bg-[#007AFF]',
      'amazon': 'bg-[#FF9900]',
      'deezer': 'bg-[#FF5500]',
      'tiktok': 'bg-[#FE2C55]',
      'youtube': 'bg-[#FF0000]',
      'shazam': 'bg-[#0066FF]',
      'tracklist': 'bg-[#8B5CF6]',
      'beatport': 'bg-[#01FF95]',
      'traxsource': 'bg-[#FF8C00]',
      'itunes': 'bg-[#A855F7]',
      'tidal': 'bg-[#000000]',
      'soundcloud': 'bg-[#FF5500]',
      'billboard': 'bg-[#FF6B35]',
      'other': 'bg-[rgba(255,255,255,0.2)]'
    };
    return colors[platform?.toLowerCase()] || 'bg-[rgba(255,255,255,0.2)]';
  };

  const getChartSourceStyle = (platform) => {
    const styles = {
      'spotify': 'bg-[rgba(29,185,84,0.2)] text-[#1DB954]',
      'apple_music': 'bg-[rgba(0,122,255,0.2)] text-[#007AFF]',
      'amazon': 'bg-[rgba(255,153,0,0.2)] text-[#FF9900]',
      'deezer': 'bg-[rgba(255,85,0,0.2)] text-[#FF5500]',
      'tiktok': 'bg-[rgba(254,44,85,0.2)] text-[#FE2C55]',
      'youtube': 'bg-[rgba(255,0,0,0.2)] text-[#FF0000]',
      'shazam': 'bg-[rgba(0,102,255,0.2)] text-[#0066FF]',
      'tracklist': 'bg-[rgba(139,92,246,0.2)] text-[#8B5CF6]',
      'beatport': 'bg-[rgba(1,255,149,0.2)] text-[#01FF95]',
      'traxsource': 'bg-[rgba(255,140,0,0.2)] text-[#FF8C00]',
      'itunes': 'bg-[rgba(168,85,247,0.2)] text-[#A855F7]',
      'tidal': 'bg-[rgba(0,0,0,0.3)] text-white',
      'soundcloud': 'bg-[rgba(255,85,0,0.2)] text-[#FF5500]',
      'billboard': 'bg-[rgba(255,107,53,0.2)] text-[#FF6B35]',
      'other': 'bg-[rgba(255,255,255,0.1)] text-white'
    };
    return styles[platform?.toLowerCase()] || 'bg-[rgba(255,255,255,0.1)] text-white';
  };

  const getChartSourceName = (chart) => {
    const platform = chart.platform?.toLowerCase();
    const chartName = chart.chart_name || chart.activity_text || '';
    const lowerChartName = chartName.toLowerCase();
    
    // Extract specific chart names from activity text
    if (lowerChartName.includes('hot 100')) return 'Billboard Hot 100';
    if (lowerChartName.includes('top 200')) return 'Top 200';
    if (lowerChartName.includes('top 50')) return 'Top 50';
    if (lowerChartName.includes('viral 50')) return 'Viral 50';
    if (lowerChartName.includes('global')) return 'Global Chart';
    
    // Beatport specific charts
    if (lowerChartName.includes('beatport')) {
      if (lowerChartName.includes('top 100')) return 'Beatport Top 100';
      if (lowerChartName.includes('techno')) return 'Beatport Techno';
      if (lowerChartName.includes('house')) return 'Beatport House';
      if (lowerChartName.includes('trance')) return 'Beatport Trance';
      if (lowerChartName.includes('dubstep')) return 'Beatport Dubstep';
      if (lowerChartName.includes('bass house')) return 'Bass House';
      if (lowerChartName.includes('tech house')) return 'Tech House Releases';
      return 'Beatport Chart';
    }
    
    // 1001 Tracklists specific
    if (lowerChartName.includes('1001') || lowerChartName.includes('tracklist')) {
      return '1001 Tracklists';
    }
    
    // TikTok specific
    if (lowerChartName.includes('tiktok') || lowerChartName.includes('viral')) {
      return 'TikTok Viral';
    }
    
    // Shazam specific
    if (lowerChartName.includes('shazam') || lowerChartName.includes('discovery')) {
      return 'Shazam Discovery';
    }
    
    // Genre-specific charts (simplified to match what we see in the image)
    if (lowerChartName.includes('house releases')) return 'House Releases';
    if (lowerChartName.includes('latin')) return 'Latin: Suriname';
    if (lowerChartName.includes('costa rica')) return 'Music: Costa Rica';
    
    // Platform-specific defaults - simplified
    switch(platform) {
      case 'spotify': return 'Spotify Chart';
      case 'apple_music': return 'Apple Music Chart'; 
      case 'amazon': return 'Amazon Chart';
      case 'deezer': return 'Deezer Chart';
      case 'tiktok': return 'TikTok Chart';
      case 'youtube': return 'YouTube Chart';
      case 'shazam': return 'Shazam Chart';
      case 'tracklist': return '1001 Tracklists';
      case 'beatport': return 'Beatport Chart';
      case 'traxsource': return 'Traxsource Chart';
      case 'itunes': return 'iTunes Chart';
      case 'tidal': return 'Tidal Chart';
      case 'soundcloud': return 'SoundCloud Chart';
      case 'billboard': return 'Billboard';
      default: 
        // Try to extract meaningful chart name from activity text
        if (chartName && chartName.length > 5) {
          return chartName;
        }
        return 'Chart';
    }
  };

  const getPlatformDisplayName = (platform) => {
    const displayNames = {
      'spotify': 'Spotify',
      'apple_music': 'Apple',
      'amazon': 'Amazon',
      'deezer': 'Deezer', 
      'tiktok': 'TikTok',
      'youtube': 'YouTube',
      'shazam': 'Shazam',
      'tracklist': '1001TL',
      'beatport': 'Beatport',
      'traxsource': 'Traxsource',
      'itunes': 'iTunes',
      'tidal': 'Tidal',
      'soundcloud': 'SoundCloud',
      'billboard': 'Billboard',
      'other': 'Other'
    };
    return displayNames[platform?.toLowerCase()] || 'Other';
  };

  const formatChartDescription = (chart) => {
    const activityText = chart.activity_text || chart.chart_name || '';
    const platform = chart.platform?.toLowerCase();
    
    // If we have detailed activity text, use it
    if (activityText && activityText.length > 20) {
      return activityText;
    }
    
    // Generate descriptive text based on available data
    let description = '';
    
    if (chart.position) {
      if (chart.previous_position) {
        const change = chart.position - chart.previous_position;
        if (change < 0) {
          description = `Climbed ${Math.abs(change)} spots to #${chart.position}`;
        } else if (change > 0) {
          description = `Dropped ${change} spots to #${chart.position}`;
        } else {
          description = `Holding steady at #${chart.position}`;
        }
      } else {
        description = `Charted #${chart.position}`;
      }
    }
    
    // Add chart source context
    const chartSource = getChartSourceName(chart);
    if (chartSource !== 'Music Chart') {
      description += ` on ${chartSource}`;
    }
    
    // Add regional context if available
    if (chart.country && chart.country !== 'Global') {
      description += ` (${chart.country})`;
    }
    
    return description || activityText || 'Chart activity';
  };



  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'spotify', label: 'Spotify' },
    { id: 'apple_music', label: 'Apple' },
    { id: 'billboard', label: 'Billboard' },
    { id: 'youtube', label: 'YouTube' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'shazam', label: 'Shazam' },
    { id: 'tracklist', label: '1001TL' },
    { id: 'beatport', label: 'Beatport' },
    { id: 'other', label: 'Other' }
  ];

  const filteredChartData = chartData.filter(chart => {
    // Filter by platform tab
    let passesTab = true;
    if (activeTab !== 'all') {
      passesTab = chart.platform === activeTab;
    }
    
    // Filter by selected artist
    let passesArtist = true;
    if (selectedArtist !== null) {
      passesArtist = chart.artist_name === selectedArtist;
    }
    
    return passesTab && passesArtist;
  });

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[16px] font-bold mb-2 text-left">
                Loading chart data...
              </div>
              <div className="text-[rgba(255,255,255,0.7)] text-[12px] mb-4 max-w-[300px]">
                Fetching chart positions from SongStats across all platforms...
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9500]"></div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <p className="text-white text-[12px]">Charting</p>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowArtistSearch(!showArtistSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Artist
              </button>
              <button 
                onClick={loadChartData}
                disabled={refreshingAll}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50 flex items-center gap-1"
              >
                {refreshingAll ? (
                  <>
                    <div className="animate-spin rounded-full h-2 w-2 border-b border-white"></div>
                    Refreshing...
                  </>
                ) : (
                  <>Refresh All</>
                )}
              </button>
            <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex border-b border-[rgba(255,255,255,0.1)] overflow-x-auto scrollbar-hide px-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FF9500]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Artist Search Interface */}
          {showArtistSearch && (
            <div className="w-full px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-white text-[12px] font-semibold">Search Any Artist</span>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchArtists(e.target.value);
                    }}
                    placeholder="Enter artist name to search..."
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                  />
                  
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-[#FF9500]"></div>
                    </div>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="mt-3 max-h-[200px] overflow-y-auto">
                    {searchResults.map((artist, index) => (
                      <div
                        key={`${artist.spotifyUrl}-${index}`}
                        className="flex items-center gap-3 p-3 hover:bg-[rgba(255,153,0,0.1)] cursor-pointer transition-colors rounded-lg"
                        onClick={() => selectSearchedArtist(artist)}
                      >
                        {artist.image && (
                          <img 
                            src={artist.image} 
                            alt={artist.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <div className="text-white text-[12px] font-semibold">
                            {artist.name}
                          </div>
                          <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                            {artist.followers ? `${artist.followers.toLocaleString()} followers` : 'Spotify Artist'}
                          </div>
                        </div>
                        {artist.verified && (
                          <div className="text-[#FF9500] text-[8px] bg-[rgba(255,153,0,0.2)] px-2 py-1 rounded">
                            Popular
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                  <div className="mt-3 text-[rgba(255,255,255,0.7)] text-[10px] p-2">
                    No artists found for "{searchQuery}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Artist Filter Tags */}
          {catalogArtists.length > 0 && (
            <div className="w-full px-4 mb-6">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {/* All Artists Tag */}
                <button
                  onClick={() => setSelectedArtist(null)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors flex items-center gap-2 ${
                    selectedArtist === null 
                      ? 'bg-[#FF9500] text-black' 
                      : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                  }`}
                >
                  All Artists
                </button>
                
                {/* Individual Artist Tags */}
                {catalogArtists.map((artist, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedArtist(artist.name)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors flex items-center gap-2 ${
                      selectedArtist === artist.name 
                        ? 'bg-[#FF9500] text-black' 
                        : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                    }`}
                  >
                    {artist.name}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshArtistData(artist.name);
                      }}
                      disabled={refreshingArtists.has(artist.name)}
                      className="w-4 h-4 flex items-center justify-center rounded text-current hover:bg-[rgba(0,0,0,0.1)] transition-colors disabled:opacity-50"
                    >
                      {refreshingArtists.has(artist.name) ? (
                        <div className="animate-spin rounded-full h-2 w-2 border-b border-current"></div>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                    </button>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {filteredChartData.length === 0 ? (
              <div className="text-left py-[60px]">
                {chartData.length === 0 && !loading ? (
                  <>
                    <h3 className="text-white text-[12px] font-semibold mb-2">
                      Ready to Load Chart Data
                    </h3>
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                      Click "Load Chart Data" to fetch chart positions from your catalog artists.
                    </p>
                    <button 
                      onClick={loadChartData}
                      disabled={refreshingAll}
                      className="bg-[#FF9500] text-black px-5 py-2 rounded-lg text-[12px] hover:bg-[#FF9500CC] transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {refreshingAll ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-black"></div>
                          Refreshing...
                        </>
                      ) : (
                        <>Load Chart Data</>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No chart data found
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                      {selectedArtist ? `No chart data for ${selectedArtist}` : 'No chart data for current filters'}
                      {activeTab !== 'all' ? ` on ${activeTab}` : ''}. 
                      Try switching filters or refresh to see if new data is available.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredChartData.map((chart, index) => (
                  <div 
                    key={chart._id || index}
                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,153,0,0.05)] transition-all"
                  >
                    <div className="flex items-start gap-4">
                      {/* Platform Icon & Chart Position */}
                      <div className="flex flex-col items-center justify-center w-[80px] flex-shrink-0">
                        {/* Platform Icon */}
                        <div className={`w-6 h-6 rounded flex items-center justify-center mb-1 ${getPlatformColor(chart.platform)}`}>
                          <span className="text-white text-[10px] font-bold">
                            {getPlatformIcon(chart.platform)}
                          </span>
                        </div>
                      {/* Chart Position */}
                        <span className="text-[#FF9500] text-[18px] font-bold">
                          #{chart.position || '?'}
                        </span>
                        {/* Platform Name */}
                        <span className="text-[rgba(255,255,255,0.5)] text-[8px] uppercase font-semibold mt-1">
                          {getPlatformDisplayName(chart.platform)}
                        </span>
                      </div>
                      
                      {/* Track Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white text-[14px] font-semibold mb-1 break-words">
                          {chart.track_name || 'Unknown Track'}
                        </h3>
                        
                        <p className="text-[rgba(255,255,255,0.5)] text-[12px] mb-2 break-words">
                          {chart.artist_name || 'Unknown Artist'}
                          {chart.country && chart.country !== 'Global' && (
                            <span className="text-[rgba(255,255,255,0.4)]"> • {chart.country}</span>
                          )}
                          {chart.artist_tracks_count > 1 && (
                            <span className="text-[rgba(255,153,0,0.7)]"> • {chart.artist_tracks_count} tracks</span>
                          )}
                        </p>
                        
                        {/* Chart Description */}
                        <p className="text-[rgba(255,255,255,0.4)] text-[11px] mb-2 leading-tight break-words">
                          {formatChartDescription(chart)}
                        </p>
                        
                        {/* Enhanced Tags */}
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Movement Badge */}
                          {chart.previous_position && (
                            <div className={`px-2 py-1 rounded text-[10px] font-semibold ${
                              chart.position < chart.previous_position 
                                ? 'bg-[rgba(34,197,94,0.2)] text-green-400' 
                                : 'bg-[rgba(239,68,68,0.2)] text-red-400'
                            }`}>
                              {chart.position < chart.previous_position ? 'UP' : 'DOWN'} 
                              {Math.abs(chart.position - chart.previous_position)}
                            </div>
                          )}
                          
                          {/* Chart Tenure */}
                          {chart.weeks_on_chart && (
                            <div className="bg-[rgba(99,102,241,0.2)] px-2 py-1 rounded text-[10px] text-indigo-400 font-semibold">
                              {chart.weeks_on_chart} week{chart.weeks_on_chart !== 1 ? 's' : ''}
                            </div>
                          )}

                          {/* Peak Position */}
                          {chart.peak_position && chart.peak_position !== chart.position && (
                            <div className="bg-[rgba(168,85,247,0.2)] px-2 py-1 rounded text-[10px] text-purple-400 font-semibold">
                              Peak: #{chart.peak_position}
                            </div>
                          )}

                          {/* Platform-Specific Features */}
                          {chart.platform === 'spotify' && chart.activity_text?.includes('viral') && (
                            <div className="bg-[rgba(29,185,84,0.2)] px-2 py-1 rounded text-[10px] text-[#1DB954] font-semibold">
                              Viral
                            </div>
                          )}

                          {chart.platform === 'billboard' && chart.chart_name?.includes('Hot 100') && (
                            <div className="bg-[rgba(255,107,53,0.2)] px-2 py-1 rounded text-[10px] text-[#FF6B35] font-semibold">
                              Hot 100
                            </div>
                          )}

                          {chart.platform === 'beatport' && chart.chart_name?.toLowerCase().includes('top 100') && (
                            <div className="bg-[rgba(1,255,149,0.2)] px-2 py-1 rounded text-[10px] text-[#01FF95] font-semibold">
                              Top 100
                            </div>
                          )}

                          {chart.platform === 'tracklist' && (
                            <div className="bg-[rgba(139,92,246,0.2)] px-2 py-1 rounded text-[10px] text-[#8B5CF6] font-semibold">
                              DJ Charts
                            </div>
                          )}

                          {chart.platform === 'tiktok' && (
                            <div className="bg-[rgba(254,44,85,0.2)] px-2 py-1 rounded text-[10px] text-[#FE2C55] font-semibold">
                              Viral
                            </div>
                          )}

                          {chart.platform === 'shazam' && (
                            <div className="bg-[rgba(0,102,255,0.2)] px-2 py-1 rounded text-[10px] text-[#0066FF] font-semibold">
                              Discovery
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </LayoutHeader>
  );
};

export default ChartingApp;